'use client';

import { signOut, useSession } from 'next-auth/react';
import { useLogoutMutation } from '@akinon/next/data/client/user';
import { ROUTES } from '@theme/routes';

import { Icon, Link } from '@theme/components';
import { usePathname } from 'next/navigation';
import clsx from 'clsx';
import { useLocalization } from '@akinon/next/hooks';
import { twMerge } from 'tailwind-merge';

const ACCOUNT_MENU_ITEMS = [
  {
    translationKey: 'account.base.menu.my_orders',
    href: ROUTES.ACCOUNT_ORDERS,
    testId: 'account-orders'
  },
  {
    translationKey: 'account.base.menu.my_profile.title',
    href: ROUTES.ACCOUNT_PROFILE,
    testId: 'account-profile'
  },
  {
    translationKey: 'account.base.menu.address_book',
    href: ROUTES.ACCOUNT_ADDRESS,
    testId: 'account-address'
  },

  {
    translationKey: 'account.base.menu.my_wishlist',
    href: ROUTES.ACCOUNT_WISHLIST,
    testId: 'account-wishlist'
  },
  {
    translationKey: 'account.base.menu.my_vouchers',
    href: ROUTES.ACCOUNT_COUPONS,
    testId: 'account-coupons'
  },
  {
    translationKey: 'account.base.menu.price_alerts',
    href: ROUTES.ACCOUNT_PRICEALERTS,
    testId: 'account-price-alerts'
  },
  {
    translationKey: 'account.base.menu.stock_alerts',
    href: ROUTES.ACCOUNT_STOCKALERT,
    testId: 'account-stock-alerts'
  }
];

export default function AccountMenu({
  showNavbarMobile = true
}: {
  showNavbarMobile?: boolean;
}) {
  const { t } = useLocalization();
  const { data: session, status } = useSession();
  const [logout] = useLogoutMutation();
  const pathname = usePathname();
  const accountMenuRegex = new RegExp(`^${ROUTES.ACCOUNT}/?$`).test(pathname);
  const handleLogout = async () => {
    logout()
      .unwrap()
      .then(() => {
        signOut({
          callbackUrl: '/'
        });
      })
      .catch();
  };

  const hideMenu = pathname?.match(/^\/users\/orders\/\d+(\/.*)?$/);

  if (hideMenu) return null;

  return (
    <div
      className={clsx(
        'w-full bg-gray-75 h-full lg:bg-gray-75/35 lg:px-[5%] flex flex-col lg:gap-8'
      )}
    >
      <div className="flex flex-col items-start gap-1 lg:gap-2 py-8 px-4 lg:px-0 lg:py-0">
        {status === 'loading' ? (
          <div className="h-8 lg:h-[2rem] w-48 bg-gray-200 animate-pulse rounded lg:mt-18"></div>
        ) : (
          status === 'authenticated' && (
            <h2 className="font-medium leading-[1.2] text-xl lg:text-[2rem] lg:mt-18">
              {`${session?.user?.firstName} ${session?.user?.lastName}`}
            </h2>
          )
        )}
        <button
          className="underline text-sm leading-none text-gray h-8"
          data-testid="account-logout"
          onClick={handleLogout}
        >
          {t('account.base.menu.logout')}
        </button>
      </div>

      <ul
        className={twMerge(
          'lg:flex flex-col lg:flex-row items-start px-4 lg:px-0 lg:items-center',
          showNavbarMobile ? 'flex' : 'hidden'
        )}
      >
        {ACCOUNT_MENU_ITEMS.map((item) => (
          <li
            key={item.testId}
            className={twMerge(
              'text-left lg:text-center font-medium lg:font-normal text-sm lg:text-lg flex-1 border-b border-gray-250 w-full',
              pathname === item.href &&
                'font-medium text-primary-black border-primary-black'
            )}
          >
            <Link
              href={item.href}
              data-testid={item.testId}
              className="w-full h-full py-[25px] lg:py-[23px] flex items-center justify-between lg:justify-center"
            >
              {t(item.translationKey)}
              <Icon className="lg:hidden" name="chevron-right" size={24} />
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
